import React, { useState } from "react";
import { PromotionPlanSummary } from "../../global/Types";

const JIRA_BASE_URL = "https://outthink.atlassian.net";

const TicketLink = ({ ticket }: { ticket: string }) => {
    const url = `${JIRA_BASE_URL}/browse/${ticket}`;
    return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline">
            {ticket}
        </a>
    );
};


interface PromotionPlanSummaryBarProps {
    summary: PromotionPlanSummary;
}

const PromotionPlanSummaryBar: React.FC<PromotionPlanSummaryBarProps> = ({ summary }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const hasWarnings = summary.tickets_not_in_fix_version.length > 0 ||
        summary.unticketed_commits > 0 ||
        summary.tickets_with_no_commits.length > 0;

    return (
        <div className={`mb-4 rounded-lg border bg-white shadow-sm px-4 py-3 ${hasWarnings ? 'border-amber-200' : 'border-indigo-100'}`}>
            {/* Collapsed row */}
            <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm font-semibold text-indigo-700">
                    {summary.total_repos} repo{summary.total_repos !== 1 ? "s" : ""}
                </span>
                <div className="w-px h-4 bg-indigo-100" />
                <span className="flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-sm text-green-700">{summary.tickets_in_fix_version.length} in fix version</span>
                </span>
                {summary.tickets_not_in_fix_version.length > 0 && (
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                        <span className="text-sm text-amber-700">{summary.tickets_not_in_fix_version.length} not in fix version</span>
                    </span>
                )}
                {summary.unticketed_commits > 0 && (
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2 h-2 rounded-full bg-red-400" />
                        <span className="text-sm text-red-700">{summary.unticketed_commits} unticketed commit{summary.unticketed_commits !== 1 ? "s" : ""}</span>
                    </span>
                )}
                {summary.tickets_with_no_commits.length > 0 && (
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2 h-2 rounded-full bg-gray-400" />
                        <span className="text-sm text-gray-600">{summary.tickets_with_no_commits.length} ticket{summary.tickets_with_no_commits.length !== 1 ? "s" : ""} with no commits</span>
                    </span>
                )}
                <button
                    className="ml-auto text-xs text-indigo-500 hover:text-indigo-700 font-medium flex items-center gap-1 transition-colors"
                    onClick={() => setIsExpanded(prev => !prev)}
                >
                    {isExpanded ? "▲" : "▼"}
                </button>
            </div>

            {/* Expanded breakdown */}
            {isExpanded && (
                <div className="mt-3 pt-3 border-t border-indigo-50 flex flex-wrap gap-8">
                    {/* In Fix Version */}
                    <div className="min-w-max">
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                            <span className="text-xs font-semibold uppercase tracking-wide text-green-700">
                                In Fix Version ({summary.tickets_in_fix_version.length})
                            </span>
                        </div>
                        <div className="flex flex-col gap-1 pl-3.5">
                            {summary.tickets_in_fix_version.map(ticket => (
                                // <span key={ticket} className="text-xs text-gray-600">{ticket}</span>
                                <TicketLink key={ticket} ticket={ticket} />
                            ))}
                        </div>
                    </div>

                    {/* Not In Fix Version */}
                    {summary.tickets_not_in_fix_version.length > 0 && (
                        <div className="min-w-max">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                                <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                                    Not In Fix Version ({summary.tickets_not_in_fix_version.length})
                                </span>
                            </div>
                            <div className="flex flex-col gap-1 pl-3.5">
                                {summary.tickets_not_in_fix_version.map(ticket => (
                                    // <span key={ticket} className="text-xs text-gray-600">{ticket}</span>
                                    <TicketLink key={ticket} ticket={ticket} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Unticketed */}
                    {summary.unticketed_commits > 0 && (
                        <div className="min-w-max">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <span className="inline-block w-2 h-2 rounded-full bg-red-400" />
                                <span className="text-xs font-semibold uppercase tracking-wide text-red-700">
                                    Unticketed Commits ({summary.unticketed_commits})
                                </span>
                            </div>
                            <div className="pl-3.5">
                                <span className="text-xs text-gray-500">No ticket numbers to display</span>
                            </div>
                        </div>
                    )}

                    {/* No Commits Found */}
                    {summary.tickets_with_no_commits.length > 0 && (
                        <div className="min-w-max">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <span className="inline-block w-2 h-2 rounded-full bg-gray-400" />
                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                                    No Commits Found ({summary.tickets_with_no_commits.length})
                                </span>
                            </div>
                            <div className="flex flex-col gap-1 pl-3.5">
                                {summary.tickets_with_no_commits.map(ticket => (
                                    // <span key={ticket} className="text-xs text-gray-600">{ticket}</span>
                                    <TicketLink key={ticket} ticket={ticket} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PromotionPlanSummaryBar;